import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Canvas, IText, Rect, Circle, Image as FabricImage, filters, Group, ActiveSelection } from 'fabric';
import {
  Box,
  Paper,
  Button,
  IconButton,
  Toolbar,
  Typography,
  Divider,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Image as ImageIcon,
  Type,
  Square,
  Circle as CircleIcon,
  Trash2,
  Download,
  Bold,
  Italic,
  ChevronUp,
  ChevronDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Droplet,
  ZoomIn,
  ZoomOut,
  MousePointer2,
  Group as GroupIcon,
  Ungroup,
  Copy,
  ClipboardPaste
} from 'lucide-react';
import {
  Slider,
  TextField,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CompressionDialog from './CompressionDialog';
import * as fabric from 'fabric';

const fonts = [
  'Arial', 'Verdana', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Impact',
  'Comic Sans MS' // Just for fun
];

const EditImages = () => {
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [selectedObject, setSelectedObject] = useState(null);

  // --- State for Properties Panel ---
  const [opacity, setOpacity] = useState(1);
  const [fillColor, setFillColor] = useState('#000000');
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [fontSize, setFontSize] = useState(40);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontStyle, setFontStyle] = useState([]);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [textAlign, setTextAlign] = useState('left');
  const [shadowColor, setShadowColor] = useState('#000000');
  const [shadowBlur, setShadowBlur] = useState(0);
  const [shadowOffsetX, setShadowOffsetX] = useState(0);
  const [shadowOffsetY, setShadowOffsetY] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [compressionDialogOpen, setCompressionDialogOpen] = useState(false);
  const [downloadSettings, setDownloadSettings] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [notification, setNotification] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // --- Update Properties Panel based on Selection ---
  const updatePropertiesPanel = (obj) => {
    if (!obj) {
      // Reset to defaults or clear if preferred when nothing is selected
      setOpacity(1);
      setFillColor('#000000');
      setStrokeColor('#000000');
      setStrokeWidth(0);
      setFontSize(40);
      setFontFamily('Arial');
      setFontStyle([]);
      setBrightness(0);
      setContrast(0);
      setSaturation(0);
      setTextAlign('left');
      setShadowColor('#000000');
      setShadowBlur(0);
      setShadowOffsetX(0);
      setShadowOffsetY(0);
      return;
    }

    // Helper function to convert color to hex
    const toHex = (colorString) => {
      if (!colorString || typeof colorString !== 'string') return '#000000'; // Default to black if invalid
      try {
        // Use Fabric's built-in color conversion and ensure # prefix
        const hex = new fabric.Color(colorString).toHex();
        return `#${hex}`; // Prepend #
      } catch (e) {
        console.warn(`Failed to convert color ${colorString} to hex:`, e);
        return '#000000'; // Fallback on error
      }
    };

    setOpacity(obj.get('opacity') ?? 1);
    // Ensure colors are set in hex format for the color input fields
    setFillColor(toHex(obj.get('fill'))); 
    setStrokeColor(toHex(obj.get('stroke')));
    setStrokeWidth(obj.get('strokeWidth') ?? 0);

    if (obj.type === 'i-text') {
      setFontSize(obj.get('fontSize') ?? 40);
      setFontFamily(obj.get('fontFamily') ?? 'Arial');
      const styles = [];
      if (obj.get('fontWeight') === 'bold') styles.push('bold');
      if (obj.get('fontStyle') === 'italic') styles.push('italic');
      setFontStyle(styles);
      setTextAlign(obj.get('textAlign') ?? 'left');
    } else {
       setFontSize(40); // Reset font size if not text
       setFontFamily('Arial'); // Reset font family
       setFontStyle([]);     // Reset styles
       setTextAlign('left'); // Reset alignment
    }

    // Update shadow state
    const shadow = obj.shadow;
    if (shadow) {
      setShadowColor(shadow.color || '#000000');
      setShadowBlur(shadow.blur || 0);
      setShadowOffsetX(shadow.offsetX || 0);
      setShadowOffsetY(shadow.offsetY || 0);
    } else {
      setShadowColor('#000000');
      setShadowBlur(0);
      setShadowOffsetX(0);
      setShadowOffsetY(0);
    }

    // Handle filters for images
    if (obj instanceof FabricImage) {
      const currentFilters = obj.filters || [];
      setBrightness(currentFilters.find(f => f.type === 'Brightness')?.brightness || 0);
      setContrast(currentFilters.find(f => f.type === 'Contrast')?.contrast || 0);
      setSaturation(currentFilters.find(f => f.type === 'Saturation')?.saturation || 0);
    } else {
      // Reset filters if not an image
      setBrightness(0);
      setContrast(0);
      setSaturation(0);
    }
  };

  // Initialize Fabric Canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    // Use a small delay to allow DOM to potentially stabilize
    const timerId = setTimeout(() => {
      if (!canvasRef.current) return; // Re-check ref

      const fabricCanvas = new Canvas(canvasRef.current, {
        width: canvasSize.width,
        height: canvasSize.height,
        backgroundColor: '#f0f0f0',
        preserveObjectStacking: true,
      });

      // --- Event Listeners --- 
      const handleSelection = (e) => {
        // On multi-select, e.selected might have multiple objects.
        // For simplicity, we'll focus on the first selected object.
        setSelectedObject(e.selected[0]);
        updatePropertiesPanel(e.selected[0]); // <-- Update panel on selection
      };
      
      fabricCanvas.on('selection:created', handleSelection);
      fabricCanvas.on('selection:updated', handleSelection);
      fabricCanvas.on('selection:cleared', () => {
        setSelectedObject(null);
        updatePropertiesPanel(null); // <-- Clear panel on deselection
      });

      setCanvas(fabricCanvas);

      // Center canvas initially if container ref is available
      if (canvasContainerRef.current) {
          // Logic to center canvas can be added here if needed
      }

    }, 10); // 10ms delay

    return () => {
      clearTimeout(timerId);
      // Dispose canvas instance if it was created
      // Needs careful handling if canvas state update is async
      if (canvas) {
        canvas.dispose();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Update canvas dimensions when state changes
  useEffect(() => {
    if (canvas) {
      canvas.setDimensions({ width: canvasSize.width, height: canvasSize.height });
      canvas.renderAll();
    }
  }, [canvas, canvasSize]);

  // --- Apply Properties from Panel to Canvas Object ---
  useEffect(() => {
    if (canvas && selectedObject) {
      selectedObject.set({
        opacity: opacity,
        fill: fillColor, 
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        // Conditionally set text properties
        ...(selectedObject.type === 'i-text' && { 
            fontSize: fontSize, 
            fontFamily: fontFamily,
            fontWeight: fontStyle.includes('bold') ? 'bold' : 'normal',
            fontStyle: fontStyle.includes('italic') ? 'italic' : 'normal',
            textAlign: textAlign
         })
      });

      // Construct and apply shadow object
      const shadowOptions = {
        color: shadowColor,
        blur: shadowBlur,
        offsetX: shadowOffsetX,
        offsetY: shadowOffsetY,
      };
      // Apply shadow only if it has non-default values
      if (shadowColor !== '#000000' || shadowBlur > 0 || shadowOffsetX !== 0 || shadowOffsetY !== 0) {
        selectedObject.set('shadow', shadowOptions);
      } else {
        selectedObject.set('shadow', null); // Remove shadow if values are default
      }

      // Apply filters specifically for images
      if (selectedObject instanceof FabricImage) {
        const activeFilters = [];
        if (brightness !== 0) activeFilters.push(new filters.Brightness({ brightness }));
        if (contrast !== 0) activeFilters.push(new filters.Contrast({ contrast }));
        if (saturation !== 0) activeFilters.push(new filters.Saturation({ saturation }));

        selectedObject.filters = activeFilters;
        selectedObject.applyFilters(); // Important: Apply the filters
      }

      canvas.renderAll();
    }
  }, [canvas, selectedObject, opacity, fillColor, strokeColor, strokeWidth, fontSize, fontFamily, fontStyle, brightness, contrast, saturation, textAlign, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY]);

  // --- Layering Functions ---
  const bringObjectForward = () => {
    if (!canvas || !selectedObject) return;
    canvas.bringObjectForward(selectedObject);
    canvas.renderAll();
  };

  const sendObjectBackwards = () => {
    if (!canvas || !selectedObject) return;
    canvas.sendObjectBackwards(selectedObject);
    canvas.renderAll();
  };

  const bringObjectToFront = () => {
    if (!canvas || !selectedObject) return;
    canvas.bringObjectToFront(selectedObject);
    canvas.renderAll();
  };
  
  const sendObjectToBack = () => {
    if (!canvas || !selectedObject) return;
    canvas.sendObjectToBack(selectedObject);
    canvas.renderAll();
  };

  // --- Zoom Functions ---
  const handleZoom = (factor) => {
    if (!canvas) return;
    const newZoom = canvas.getZoom() * factor;
    // Prevent zooming too far in or out
    if (newZoom > 20 || newZoom < 0.1) return; 

    const center = canvas.getCenter();
    canvas.zoomToPoint({ x: center.left, y: center.top }, newZoom);
    setZoomLevel(newZoom); // Update state
  };

  // --- Toolbar Action Handlers ---
  const handleAddImage = () => {
    // Create a temporary file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    // Set up the change handler
    input.onchange = (e) => {
      console.log('File input onChange fired');
      const file = e.target.files[0];
      if (!file) {
        console.log('No file selected');
        return;
      }
      if (!canvas) {
        console.log('Canvas not initialized');
        return;
      }

      console.log('Starting file read');
      const reader = new FileReader();
      reader.onload = (event) => {
        console.log('FileReader onload fired');
        const imgUrl = event.target.result;
        console.log('Image URL created, attempting to load with Fabric.js');
        
        // Create a new image element
        const imgElement = new Image();
        imgElement.onload = () => {
          console.log('Image element loaded successfully');
          // Create a new Fabric image from the loaded image element
          const fabricImage = new FabricImage(imgElement, {
            left: canvas.width / 2,
            top: canvas.height / 2,
            originX: 'center',
            originY: 'center',
            selectable: true,
            hasControls: true,
          });

          // Scale image to fit canvas nicely
          const maxDim = Math.min(canvas.width * 0.8, canvas.height * 0.8);
          const scale = Math.min(maxDim / fabricImage.width, maxDim / fabricImage.height);
          fabricImage.scale(scale);

          console.log('Adding image to canvas');
          canvas.add(fabricImage);
          canvas.setActiveObject(fabricImage);
          canvas.renderAll();
          console.log('Image added and canvas rendered');
        };

        imgElement.onerror = (error) => {
          console.error('Error loading image element:', error);
        };

        imgElement.src = imgUrl;
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      };

      reader.readAsDataURL(file);
    };

    // Trigger the file dialog
    input.click();
  };

  const handleAddText = () => {
    if (!canvas) return;
    const text = new IText('Edit Text', {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontSize: 40,
      fill: '#333333',
      fontFamily: 'Arial',
      selectable: true,
      hasControls: true,
    });
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
  };

  const handleAddRectangle = () => {
    if (!canvas) return;
    const rect = new Rect({
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      width: 150,
      height: 100,
      fill: '#E04C16', // Example color
      selectable: true,
      hasControls: true,
    });
    canvas.add(rect);
    canvas.setActiveObject(rect);
    canvas.renderAll();
  };

  const handleAddCircle = () => {
    if (!canvas) return;
    const circle = new Circle({
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      radius: 75,
      fill: '#3B82F6', // Example color
      selectable: true,
      hasControls: true,
    });
    canvas.add(circle);
    canvas.setActiveObject(circle);
    canvas.renderAll();
  };

  const handleDelete = useCallback(() => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject(); // Get the currently active object/selection
    if (!activeObject) return;
    
    if (activeObject.type === 'activeSelection') {
      // If it's a multi-selection, remove each object in the selection
      activeObject.forEachObject((obj) => {
        canvas.remove(obj);
      });
    } else {
      // If it's a single object
      canvas.remove(activeObject);
    }
    
    canvas.discardActiveObject(); // Deselect
    canvas.renderAll(); // Re-render the canvas
    // Note: setSelectedObject(null) will be handled by the 'selection:cleared' event listener
  }, [canvas]);

  const handleDownload = (settings) => {
    if (!canvas) return;

    // Get the canvas data URL with the specified quality settings
    const dataUrl = canvas.toDataURL({
      format: settings.options.outputFormat || 'png',
      quality: settings.type === 'lossy' ? settings.options.initialQuality : 1.0,
      multiplier: 1 // Use original resolution
    });

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `edited-image.${settings.options.outputFormat || 'png'}`;

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGroup = () => {
    if (!canvas || !selectedObject) return;
    
    // Check if we have an active selection
    if (canvas.getActiveObject() && 
        (canvas.getActiveObject().type === 'activeSelection' || 
         canvas.getActiveObject().type === 'activeselection')) {
      const group = new Group(canvas.getActiveObject().removeAll(), {
        left: canvas.getCenter().left,
        top: canvas.getCenter().top,
        originX: 'center',
        originY: 'center'
      });
      canvas.add(group);
      canvas.setActiveObject(group);
      canvas.renderAll();
    }
  };

  const handleUngroup = () => {
    if (!canvas || !selectedObject) return;
    
    // Check if we have a group
    if (selectedObject.type === 'group') {
      const objects = selectedObject.removeAll();
      canvas.remove(selectedObject);
      
      // Add each object back to the canvas individually
      objects.forEach(obj => {
        canvas.add(obj);
      });
      
      // Clear the selection
      canvas.discardActiveObject();
      setSelectedObject(null);
      canvas.renderAll();
    }
  };

  const handleMultiSelect = () => {
    if (!canvas) return;
    
    // Discard current selection and create a new active selection with all objects
    canvas.discardActiveObject();
    const sel = new ActiveSelection(canvas.getObjects(), {
      canvas: canvas,
    });
    canvas.setActiveObject(sel);
    canvas.renderAll();
  };

  const handleCopy = useCallback(async () => {
    if (!canvas || !selectedObject) return;

    try {
      // 1. Determine the object type
      const objectType = selectedObject.type;
      console.log(`[handleCopy] Object type: ${objectType}`);

      // 2. Serialize the object
      // Ensure all necessary properties are included for recreation
      const objectData = selectedObject.toObject(); 
      console.log('[handleCopy] Serialized object data:', objectData);

      // 3. Store the type and serialized data in the clipboard state
      setClipboard({ type: objectType, data: objectData });
      console.log('[handleCopy] Object data stored in clipboard state.');
      
      // Optional: Show success notification
      setNotification({
          open: true,
          severity: 'success',
          message: 'Object copied to clipboard!'
      });

    } catch (error) {
      console.error('[handleCopy] Error during copy operation:', error);
      setNotification({
        open: true,
        severity: 'error',
        message: `Failed to copy object: ${error.message}`
      });
    }
  }, [canvas, selectedObject, setNotification]);

  const handlePaste = useCallback(async () => { // Make handlePaste async
    if (!canvas || !clipboard) return;

    try {
      const { type, data } = clipboard; 
      if (!type || !data) {
          console.error('[handlePaste] Invalid clipboard data.');
          return;
      }

      console.log(`[handlePaste] Pasting object of type: ${type}`);
      let newObject = null; // This will hold the single object or the ActiveSelection
      let objectsToAdd = []; // This will hold the actual object(s) to add to canvas

      // 1. Recreate the object(s) from stored data based on type
      if (type === 'rect') {
          newObject = new Rect(data);
          objectsToAdd.push(newObject);
          console.log('[handlePaste] Recreated Rect instance.');
      } else if (type === 'i-text') {
          console.log('[handlePaste] Attempting to recreate IText using direct instantiation. Data:', data);
          try {
              if (typeof data.text === 'undefined') {
                   throw new Error('Text content is missing in clipboard data for IText');
              }
              // Create a copy of the data object and remove the 'type' property
              // as it should not be passed as an option to the constructor
              const options = { ...data };
              delete options.type; // Remove the problematic property
              
              // Pass the text content and the filtered options
              newObject = new fabric.IText(data.text, options);
              
              if (newObject) {
                  objectsToAdd.push(newObject);
                  console.log('[handlePaste] Recreated IText instance via direct instantiation.');
              } else {
                   throw new Error('Direct instantiation of IText returned null/undefined');
              }
          } catch (error) {
              console.error('[handlePaste] Error recreating IText instance via direct instantiation:', error);
              setNotification({
                  open: true,
                  severity: 'error',
                  message: `Failed to paste text object: ${error.message}`
              });
              return; // Stop further execution
          }
      } else if (type === 'circle') { // Explicit handling for Circle
          newObject = new fabric.Circle(data); // Use direct instantiation for simple shapes
          objectsToAdd.push(newObject);
          console.log('[handlePaste] Recreated Circle instance.');
      } else if (type === 'image') { // Explicit handling for Image
          newObject = await new Promise((resolve, reject) => {
              // Use fabric.Image.fromObject for images to handle src loading
              fabric.Image.fromObject(data, (instance) => {
                  if (instance) resolve(instance);
                  else reject(new Error('fabric.Image.fromObject failed during paste'));
              });
          });
          objectsToAdd.push(newObject);
          console.log('[handlePaste] Recreated Image instance.');
      } else if (type === 'activeSelection') {
          console.log('[handlePaste] Recreating ActiveSelection...');
          if (!data.objects || !Array.isArray(data.objects)) {
              throw new Error('Invalid data format for ActiveSelection paste.');
          }
          
          // Recreate each object within the selection asynchronously
          const recreatedObjects = await Promise.all(data.objects.map(objData => {
              return new Promise(async (resolve, reject) => {
                  try {
                      const className = objData.type.charAt(0).toUpperCase() + objData.type.slice(1);
                      const fabricClass = fabric[className]; // e.g., fabric['Rect']
                      if (fabricClass && fabricClass.fromObject) {
                          fabricClass.fromObject(objData, (instance) => {
                              if (instance) resolve(instance);
                              else reject(new Error(`${className}.fromObject failed for object in selection`));
                          });
                      } else {
                          reject(new Error(`Cannot recreate object of type ${objData.type} in selection. Class not found.`));
                      }
                  } catch (err) {
                      reject(err);
                  }
              });
          }));

          if (recreatedObjects.length > 0) {
              // Create a new ActiveSelection to manage the group interaction
              newObject = new ActiveSelection(recreatedObjects, { canvas: canvas });
              objectsToAdd = recreatedObjects; // Add the individual objects
              console.log(`[handlePaste] Recreated ActiveSelection with ${recreatedObjects.length} objects.`);
          } else {
              throw new Error('Failed to recreate any objects for ActiveSelection paste.');
          }
          
      } else if (type === 'group') { // Explicit handling for Group
          console.log('[handlePaste] Recreating Group...');
          if (!data.objects || !Array.isArray(data.objects)) {
              throw new Error('Invalid data format for Group paste.');
          }

          // Helper function to recreate objects
          const recreateObject = (objData) => {
              console.log(`  [recreateObject] Attempting to recreate inner object of type: ${objData.type}`);
              return new Promise(async (resolve, reject) => {
                  try {
                      const options = { ...objData };
                      delete options.type; // Remove type before passing to constructor or fromObject
                      let instance = null;

                      // Use direct named imports where possible
                      if (objData.type === 'rect') {
                          console.log(`  [recreateObject] Using imported Rect constructor...`);
                          instance = new Rect(options);
                      } else if (objData.type === 'circle') {
                          console.log(`  [recreateObject] Using imported Circle constructor...`);
                          instance = new Circle(options);
                      } else if (objData.type === 'i-text') {
                          console.log(`  [recreateObject] Using imported IText constructor...`);
                          if (typeof options.text === 'undefined') {
                              reject(new Error('Text content missing for inner IText object'));
                              return;
                          }
                          instance = new IText(options.text, options);
                      } else if (objData.type === 'image') {
                          console.log(`  [recreateObject] Using imported FabricImage.fromObject...`);
                          instance = await new Promise((res, rej) => {
                              FabricImage.fromObject(options, (imgInstance) => { // Use imported FabricImage
                                  if (imgInstance) res(imgInstance);
                                  else rej(new Error('FabricImage.fromObject failed for inner object'));
                              });
                          });
                      } else {
                          // Fallback for other types using dynamic lookup
                          const className = objData.type.charAt(0).toUpperCase() + objData.type.slice(1);
                          const fabricClass = fabric[className]; 
                          console.log(`  [recreateObject] Attempting fallback for type: ${objData.type} using dynamic class ${className}`);
                          if (fabricClass && fabricClass.fromObject) {
                              instance = await new Promise((res, rej) => {
                                  fabricClass.fromObject(options, (inst) => {
                                      if (inst) res(inst);
                                      else rej(new Error(`${className}.fromObject fallback failed`));
                                  });
                              });
                          } else {
                              reject(new Error(`Cannot recreate object of type ${objData.type} in group. Class or fromObject not found.`));
                              return;
                          }
                      }

                      if (instance) {
                           console.log(`  [recreateObject] Success for ${objData.type}.`);
                           resolve(instance);
                      } else {
                           reject(new Error(`Failed to create instance for ${objData.type} in group.`));
                      }

                  } catch (err) {
                      console.error(`  [recreateObject] Error for type ${objData.type}:`, err);
                      reject(err);
                  }
              });
          };

          // Recreate each object within the group asynchronously
          const recreatedObjects = await Promise.all(data.objects.map(recreateObject));

          if (recreatedObjects.length > 0) {
              // Create group options, excluding the 'objects' array and 'type'
              const groupOptions = { ...data };
              delete groupOptions.objects;
              delete groupOptions.type;

              // Create the new group instance with recreated objects and options
              newObject = new fabric.Group(recreatedObjects, groupOptions);
              objectsToAdd.push(newObject); // Add the group itself to the canvas
              console.log(`[handlePaste] Recreated Group with ${recreatedObjects.length} objects.`);
          } else {
              throw new Error('Failed to recreate any objects for Group paste.');
          }

      } else {
          // Fallback for OTHER single object types (e.g., Triangle, Polygon, custom types)
          console.warn(`[handlePaste] Attempting generic fallback paste for type: ${type}.`);
          const className = type.charAt(0).toUpperCase() + type.slice(1);
          const fabricClass = fabric[className];
          if (fabricClass && fabricClass.fromObject) {
              newObject = await new Promise((resolve, reject) => {
                  fabricClass.fromObject(data, (instance) => {
                      if (instance) resolve(instance);
                      else reject(new Error(`${className}.fromObject failed during paste`));
                  });
              });
              objectsToAdd.push(newObject);
              console.log(`[handlePaste] Recreated ${type} instance using generic fromObject.`);
          } else {
              console.error(`[handlePaste] Cannot recreate object of type ${type}. Class or fromObject method not found.`);
              setNotification({
                  open: true, severity: 'error',
                  message: `Pasting not supported for object type: ${type}`
              });
              return;
          }
      }

      // 2. Position and add the recreated object(s)
      if (objectsToAdd.length > 0) {
          canvas.discardActiveObject();

          // Calculate offset based on the clipboard data's original position
          const offsetX = 10;
          const offsetY = 10;

          // Apply offset and add objects to canvas
          objectsToAdd.forEach(obj => {
              obj.set({
                  left: obj.left + offsetX,
                  top: obj.top + offsetY,
                  evented: true, // Make sure it's selectable
              });
              canvas.add(obj);
          });
          
          // Select the newly pasted object or group
          if (newObject.type === 'activeSelection') {
              // For ActiveSelection, recalculate coords and set active
              newObject.setCoords(); 
              canvas.setActiveObject(newObject);
          } else {
              // For single objects, just set active
              canvas.setActiveObject(newObject); 
          }

          canvas.requestRenderAll();
          console.log('[handlePaste] Object(s) pasted successfully.');
          
          // Update clipboard data position for sequential pastes
          // Need to update the top-level data OR each object's data depending on type
          setClipboard(prev => {
              const newData = { ...prev.data };
              if (prev.type === 'activeSelection') {
                  // Update position for the group/selection itself if available
                  newData.left = (newData.left || 0) + offsetX;
                  newData.top = (newData.top || 0) + offsetY;
                  // Also update individual objects within data if needed for future pastes
                  if (newData.objects && Array.isArray(newData.objects)) {
                      newData.objects = newData.objects.map(objData => ({
                          ...objData,
                          left: (objData.left || 0) + offsetX,
                          top: (objData.top || 0) + offsetY,
                      }));
                  }
              } else {
                  // Update position for single object
                  newData.left = (newData.left || 0) + offsetX;
                  newData.top = (newData.top || 0) + offsetY;
              }
              return { ...prev, data: newData };
          });

      } else {
          console.error('[handlePaste] Failed to recreate object(s) from clipboard data.');
      }

    } catch (error) {
      console.error('Error pasting object:', error);
      setNotification({
        open: true,
        severity: 'error',
        message: `Failed to paste object: ${error.message}`
      });
    }
  }, [canvas, clipboard, setNotification]);

  // --- Add Keyboard Listener for Deletion, Copy, Paste ---
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!canvas) return;
      
      const activeObject = canvas.getActiveObject();
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable);

      // Don't trigger actions if an input is focused
      if (isInputFocused) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKeyPressed = isMac ? event.metaKey : event.ctrlKey;

      // Handle Delete/Backspace
      if (activeObject && (event.key === 'Backspace' || event.key === 'Delete')) {
        event.preventDefault(); 
        handleDelete();
        return; // Done handling this event
      }

      // Handle Copy (Cmd/Ctrl + C)
      if (modifierKeyPressed && (event.key === 'c' || event.key === 'C')) {
        if (activeObject) { // Only copy if something is selected
          event.preventDefault();
          handleCopy();
        }
        return; // Done handling this event
      }

      // Handle Paste (Cmd/Ctrl + V)
      if (modifierKeyPressed && (event.key === 'v' || event.key === 'V')) {
        if (clipboard) { // Only paste if there's something in the clipboard state
          event.preventDefault();
          handlePaste();
        }
        return; // Done handling this event
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canvas, handleDelete, handleCopy, handlePaste, clipboard]); // Added handleCopy, handlePaste, clipboard to dependencies

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', // Adjust based on header height
      overflow: 'hidden', background: '#F8FAFC' }}>

      {/* Wrapper Box for Left Toolbar to apply margin */}
      <Box sx={{ 
        height: '100%', // Takes full height of the parent flex container
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        {/* Left Toolbar */}
        <Paper
          elevation={2}
          sx={{
            width: 60,
            // height: '100%', // Let the wrapper control height positioning
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 2,
            marginTop: '64px', // <-- Push the toolbar down
            flexShrink: 0, // Prevent shrinking
            height: 'calc(100% - 64px)', // Adjust height to account for margin
            gap: 2,
            background: '#FFFFFF',
            borderRight: '1px solid #E5E7EB',
            zIndex: 10 
          }}
          // onClick={() => console.log('Toolbar Paper Clicked')}
        >
          <Tooltip title="Add Image" placement="right">
            <IconButton onClick={handleAddImage}><ImageIcon size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Tooltip title="Add Text" placement="right">
            <IconButton onClick={handleAddText}><Type size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Tooltip title="Add Rectangle" placement="right">
            <IconButton onClick={handleAddRectangle}><Square size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Tooltip title="Add Circle" placement="right">
            <IconButton onClick={handleAddCircle}><CircleIcon size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Divider sx={{ width: '80%' }} />
          <Tooltip title="Select All" placement="right">
            <IconButton onClick={handleMultiSelect}><MousePointer2 size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Tooltip title="Group Selected" placement="right">
            <IconButton onClick={handleGroup}><GroupIcon size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Tooltip title="Ungroup Selected" placement="right">
            <IconButton onClick={handleUngroup}><Ungroup size={20} color="#4B5563" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete Selected" placement="right">
            <IconButton onClick={handleDelete}><Trash2 size={20} color="#EF4444" /></IconButton>
          </Tooltip>
          <Tooltip title="Download" placement="right">
            <IconButton onClick={() => setCompressionDialogOpen(true)}><Download size={20} color="#10B981" /></IconButton>
          </Tooltip>
          <Tooltip title="Copy" placement="right">
            <IconButton 
              onClick={handleCopy} 
              disabled={!selectedObject}
            >
              <Copy size={20} color={selectedObject ? "#4B5563" : "#9CA3AF"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Paste" placement="right">
            <IconButton 
              onClick={handlePaste} 
              disabled={!clipboard}
            >
              <ClipboardPaste size={20} color={clipboard ? "#4B5563" : "#9CA3AF"} />
            </IconButton>
          </Tooltip>

          {/* Zoom Controls */}
          <Divider sx={{ width: '80%', my: 1 }} /> 
          <Tooltip title="Zoom In" placement="right">
            <IconButton onClick={() => handleZoom(1.1)}><ZoomIn size={20} color="#4B5563" /></IconButton>
          </Tooltip>
           <Typography variant="caption" sx={{ color: '#6B7280' }}>{Math.round(zoomLevel * 100)}%</Typography>
          <Tooltip title="Zoom Out" placement="right">
            <IconButton onClick={() => handleZoom(0.9)}><ZoomOut size={20} color="#4B5563" /></IconButton>
          </Tooltip>

        </Paper>
      </Box>

      {/* Canvas Area */}
      <Box
        ref={canvasContainerRef}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto', // Enable scroll if canvas is larger than view
          padding: 4, // Add some padding around the canvas
          position: 'relative' // Needed for potential overlays or absolute positioning
        }}
      >
        <Paper elevation={3} sx={{
          // This paper acts as the visual border/background for the canvas
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          // The canvas element itself will be sized by Fabric.js
          // We give the wrapper max dimensions to allow shrinking
          maxWidth: '100%',
          maxHeight: '100%',
          overflow: 'hidden', // Hide parts of canvas extending beyond paper if needed
          '& .canvas-container': { // Target Fabric's default wrapper
            margin: 'auto' // Helps centering if Paper is larger
          }
        }}>
          <canvas ref={canvasRef} />
        </Paper>
      </Box>

      {/* Right Properties Panel (Placeholder) */}
      <Paper
        elevation={2}
        sx={{
          width: 280,
          height: '100%',
          padding: 2,
          overflowY: 'auto',
          borderLeft: '1px solid #E5E7EB',
          background: '#FFFFFF',
          display: 'flex', // Keep it in layout, hide content or change width later
          flexDirection: 'column'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: 2 }}>Properties</Typography>
        <Divider sx={{ marginBottom: 2 }}/>

        {/* Canvas Size Controls - Always Visible */}
        <Box sx={{ marginBottom: 3, borderBottom: '1px dashed #E5E7EB', paddingBottom: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 1 }}>Canvas Size</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
                <TextField
                    label="Width"
                    type="number"
                    size="small"
                    value={canvasSize.width}
                    onChange={(e) => setCanvasSize(prev => ({ ...prev, width: Math.max(10, parseInt(e.target.value) || 10) }))}
                    inputProps={{ min: 10, step: 10 }}
                    sx={{ flex: 1 }}
                />
                <Typography sx={{ color: '#9CA3AF' }}>×</Typography>
                <TextField
                    label="Height"
                    type="number"
                    size="small"
                    value={canvasSize.height}
                    onChange={(e) => setCanvasSize(prev => ({ ...prev, height: Math.max(10, parseInt(e.target.value) || 10) }))}
                    inputProps={{ min: 10, step: 10 }}
                    sx={{ flex: 1 }}
                />
            </Box>
             <FormControl fullWidth size="small">
                <InputLabel id="canvas-preset-label">Presets</InputLabel>
                <MuiSelect
                    labelId="canvas-preset-label"
                    value={`${canvasSize.width}x${canvasSize.height}`}
                    label="Presets"
                    onChange={(e) => {
                        const [w, h] = e.target.value.split('x').map(Number);
                        setCanvasSize({ width: w, height: h });
                    }}
                >
                    <MenuItem value="1200x630">Facebook Post (1200x630)</MenuItem>
                    <MenuItem value="1080x1080">Instagram Post (1080x1080)</MenuItem>
                    <MenuItem value="1080x1920">Instagram Story (1080x1920)</MenuItem>
                    <MenuItem value="1280x720">YouTube Thumbnail (1280x720)</MenuItem>
                    <MenuItem value="800x600">Default (800x600)</MenuItem>
                    <MenuItem value={`${canvasSize.width}x${canvasSize.height}`}>Custom</MenuItem> 
                </MuiSelect>
            </FormControl>
        </Box>

        {selectedObject ? (
          <Box>
            {/* Opacity Slider */}
            <Box sx={{ marginBottom: 2 }}>
              <Typography gutterBottom variant="body2" sx={{ fontWeight: 500 }}>Opacity</Typography>
              <Slider
                value={typeof opacity === 'number' ? opacity : 1} // Ensure value is number
                onChange={(e, newValue) => setOpacity(newValue)}
                min={0}
                max={1}
                step={0.01}
                aria-labelledby="opacity-slider"
                valueLabelDisplay="auto"
                sx={{ color: '#E04C16' }} // Example color
              />
            </Box>

            {/* Fill Color - Common for Shapes/Text */}
            <Box sx={{ marginBottom: 2 }}>
              <Typography gutterBottom variant="body2" sx={{ fontWeight: 500 }}>Fill Color</Typography>
              <TextField
                type="color"
                value={fillColor}
                onChange={(e) => setFillColor(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ width: 20, height: 20, backgroundColor: fillColor, border: '1px solid grey' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Stroke Color & Width - Common for Shapes/Text */}
            <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
              <Box sx={{ flex: 1 }}>
                 <Typography gutterBottom variant="body2" sx={{ fontWeight: 500 }}>Stroke</Typography>
                 <TextField
                   type="color"
                   value={strokeColor}
                   onChange={(e) => setStrokeColor(e.target.value)}
                   fullWidth
                   variant="outlined"
                   size="small"
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Box sx={{ width: 20, height: 20, backgroundColor: strokeColor, border: '1px solid grey' }} />
                       </InputAdornment>
                     ),
                   }}
                 />
              </Box>
              <Box sx={{ width: '80px' }}>
                <Typography gutterBottom variant="body2" sx={{ fontWeight: 500 }}>Width</Typography>
                <TextField
                  type="number"
                  value={strokeWidth}
                  onChange={(e) => setStrokeWidth(Math.max(0, parseInt(e.target.value) || 0))} // Ensure non-negative
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, step: 1 }}
                />
              </Box>
            </Box>

            {/* Font Size - Only for Text */}
            {selectedObject.type === 'i-text' && (
              <Box sx={{ marginBottom: 2 }}>
                <Typography gutterBottom variant="body2" sx={{ fontWeight: 500 }}>Font Size</Typography>
                <TextField
                  type="number"
                  value={fontSize}
                  onChange={(e) => setFontSize(Math.max(1, parseInt(e.target.value) || 1))} // Ensure positive
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 1, step: 1 }}
                />
              </Box>
            )}

            {/* Font Family / Style - Only for Text */}
            {selectedObject.type === 'i-text' && (
              <>
                <Box sx={{ marginBottom: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="font-family-label">Font Family</InputLabel>
                    <MuiSelect
                      labelId="font-family-label"
                      value={fontFamily}
                      label="Font Family"
                      onChange={(e) => setFontFamily(e.target.value)}
                    >
                      {fonts.map(font => (
                        <MenuItem key={font} value={font} sx={{ fontFamily: font }}>{font}</MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                </Box>
                <Box sx={{ marginBottom: 2 }}>
                  <ToggleButtonGroup
                    value={fontStyle}
                    onChange={(e, newStyles) => setFontStyle(newStyles)}
                    aria-label="text formatting"
                    size="small"
                  >
                    <ToggleButton value="bold" aria-label="bold">
                      <Bold size={16} />
                    </ToggleButton>
                    <ToggleButton value="italic" aria-label="italic">
                      <Italic size={16} />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {/* Text Align */}
                <Box sx={{ marginBottom: 2 }}>
                   <Typography gutterBottom variant="caption">Alignment</Typography>
                   <ToggleButtonGroup
                    value={textAlign}
                    exclusive // Only one alignment can be active
                    onChange={(e, newAlignment) => {
                      if (newAlignment !== null) { // Prevent null value on deselect
                        setTextAlign(newAlignment);
                      }
                    }}
                    aria-label="text alignment"
                    size="small"
                  >
                    <ToggleButton value="left" aria-label="left aligned">
                      <AlignLeft size={16} />
                    </ToggleButton>
                    <ToggleButton value="center" aria-label="center aligned">
                      <AlignCenter size={16} />
                    </ToggleButton>
                    <ToggleButton value="right" aria-label="right aligned">
                      <AlignRight size={16} />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </>
            )}
            
            {/* Shadow Controls - Common */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 1 }}>Shadow</Typography>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom variant="caption">Color</Typography>
                <TextField type="color" size="small" fullWidth value={shadowColor} onChange={(e) => setShadowColor(e.target.value)} />
              </Box>
              <Box sx={{ width: '80px' }}>
                <Typography gutterBottom variant="caption">Blur</Typography>
                <TextField type="number" size="small" fullWidth value={shadowBlur} onChange={(e) => setShadowBlur(Math.max(0, parseInt(e.target.value) || 0))} inputProps={{ min: 0 }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom variant="caption">Offset X</Typography>
                <TextField type="number" size="small" fullWidth value={shadowOffsetX} onChange={(e) => setShadowOffsetX(parseInt(e.target.value) || 0)} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom variant="caption">Offset Y</Typography>
                <TextField type="number" size="small" fullWidth value={shadowOffsetY} onChange={(e) => setShadowOffsetY(parseInt(e.target.value) || 0)} />
              </Box>
            </Box>

            {/* Image Filters - Only for Images */}
            {selectedObject instanceof FabricImage && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 1 }}>Image Filters</Typography>
                <Box sx={{ marginBottom: 1 }}>
                  <Typography gutterBottom variant="caption">Brightness</Typography>
                  <Slider
                    value={brightness}
                    onChange={(e, newValue) => setBrightness(newValue)}
                    min={-1}
                    max={1}
                    step={0.01}
                    valueLabelDisplay="auto"
                    sx={{ color: '#E04C16' }}
                  />
                </Box>
                <Box sx={{ marginBottom: 1 }}>
                  <Typography gutterBottom variant="caption">Contrast</Typography>
                  <Slider
                    value={contrast}
                    onChange={(e, newValue) => setContrast(newValue)}
                    min={-1}
                    max={1}
                    step={0.01}
                    valueLabelDisplay="auto"
                    sx={{ color: '#E04C16' }}
                  />
                </Box>
                <Box sx={{ marginBottom: 1 }}>
                  <Typography gutterBottom variant="caption">Saturation</Typography>
                  <Slider
                    value={saturation}
                    onChange={(e, newValue) => setSaturation(newValue)}
                    min={-1}
                    max={1}
                    step={0.01}
                    valueLabelDisplay="auto"
                    sx={{ color: '#E04C16' }}
                  />
                </Box>
              </>
            )}

            {/* Layer Controls */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Layer</Typography>
              <Box>
                  <Tooltip title="Bring Forward">
                    <IconButton onClick={bringObjectForward} size="small">
                      <ChevronUp size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send Backward">
                    <IconButton onClick={sendObjectBackwards} size="small">
                      <ChevronDown size={18} />
                    </IconButton>
                  </Tooltip>
              </Box>
            </Box>

          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Select an object to edit its properties.</Typography>
        )}
      </Paper>

      <CompressionDialog
        open={compressionDialogOpen}
        onClose={() => setCompressionDialogOpen(false)}
        onConfirm={handleDownload}
        action="download"
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default EditImages;
